import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { AuthenticationAuthenticate } from "./Authenticate";
import { Connect } from "../Connect";
import { AuthenticationForget } from "./Forget";
import { AuthenticationReset } from "./Reset";
import { useEffect, useState } from "react";
import { useUser } from "@/hooks/useUserProvider";
import { ArrowLeftIcon } from "lucide-react";

export const Login = ({
  resetPasswordToken,
}: {
  resetPasswordToken?: string;
}) => {
  const {
    isAuthenticationModalOpen,
    setAuthenticationModalOpen,
    setCreateProfileModalOpen,
    setAccessToken,
    syncUser,
  } = useUser();
  const [activeTab, setActiveTab] = useState<"intro" | "login">("intro");
  const [activeAuthTab, setActiveAuthTab] = useState<
    "login" | "forget" | "resetPassword"
  >("login");

  const handleLogin = ({
    access_token: accessToken,
    new_user: newUser,
  }: {
    access_token: string;
    new_user: boolean;
  }) => {
    setAccessToken(accessToken);
    handleOpenChange(false);
    syncUser();

    if (newUser) {
      setCreateProfileModalOpen(true);
    }
  };

  const handleOpenChange = (value: boolean) => {
    setAuthenticationModalOpen(value);
    localStorage.setItem("unideck.auth.dismissed", "true");
  };

  useEffect(() => {
    if (isAuthenticationModalOpen) {
      if (import.meta.env.MANIFEST_VERSION) {
        setActiveTab("login");
        setActiveAuthTab("login");
      } else {
        setActiveTab("intro");
        setActiveAuthTab("login");
      }
    }
  }, [isAuthenticationModalOpen]);

  useEffect(() => {
    if (resetPasswordToken) {
      setActiveTab("login");
      setActiveAuthTab("resetPassword");
    }
  }, [resetPasswordToken]);

  return (
    <Tabs
      value={activeTab}
      onValueChange={v => setActiveTab(v as typeof activeTab)}
      className="flex w-full flex-grow flex-col"
    >
      <TabsContent
        value="intro"
        className="flex-grow animate-fade-left flex-col"
      >
        <div className="flex flex-col justify-end gap-4">
          {!import.meta.env.MANIFEST_VERSION && (
            <>
              <Connect service="google" size="lg" title="Connect with Google" />
              <Connect service="apple" size="lg" title="Connect with Apple" />
              <Connect service="gitlab" size="lg" title="Connect with Gitlab" />
              <Connect service="github" size="lg" title="Connect with Github" />
            </>
          )}
          <Button
            type="button"
            variant="default"
            size="lg"
            className="h-auto flex-grow rounded-xl bg-[#1C1C1E] py-4 text-[#FAFAFA] hover:bg-[#1C1C1E]/50 hover:text-[#FAFAFA]"
            onClick={() => {
              setActiveTab("login");
              setActiveAuthTab("login");
            }}
          >
            Login/Register
          </Button>
          <Button
            type="button"
            variant="link"
            className="-mt-2"
            onClick={() => handleOpenChange(false)}
          >
            Continue without an account
          </Button>
        </div>
      </TabsContent>
      <TabsContent
        value="login"
        className="flex-grow animate-fade-left flex-col gap-8"
      >
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 gap-3 text-sm"
            onClick={() => setActiveTab("intro")}
          >
            <ArrowLeftIcon />
            Back
          </Button>
        </div>
        <Tabs
          value={activeAuthTab}
          onValueChange={v => setActiveAuthTab(v as typeof activeAuthTab)}
          className="flex w-full flex-grow flex-col"
        >
          <TabsContent
            value="login"
            className="flex-grow animate-fade-left flex-col"
          >
            <AuthenticationAuthenticate
              onLogin={handleLogin}
              goToForget={() => setActiveAuthTab("forget")}
            />
          </TabsContent>
          <TabsContent
            value="forget"
            className="flex-grow animate-fade-left flex-col"
          >
            <AuthenticationForget goToLogin={() => setActiveAuthTab("login")} />
          </TabsContent>
          <TabsContent
            value="resetPassword"
            className="flex-grow animate-fade-left flex-col"
          >
            <AuthenticationReset
              token={resetPasswordToken}
              goToLogin={() => setActiveAuthTab("login")}
              onLogin={handleLogin}
            />
          </TabsContent>
        </Tabs>
      </TabsContent>
    </Tabs>
  );
};
